import flatten from 'lodash.flatten';
import uniqBy from 'lodash.uniqby';

import { PRINT_ASSET_TYPES } from '../../../../../constants/print-assets';
import { filterPrintAssetsByJerseyColors } from '../../../../../util/print-assets';

/**
 * Given an albumFolder and sub-filtering jersey colors, retrieve the available print background assets to present
 *
 * @param {Object} albumFolder the albumFolder to check for backdrops
 * @param {Array} jerseyColors the jersey colors available to the identity, if any
 * @returns {Array|*[]}
 */
export const getAlbumFolderPrintAssetBackgrounds = (
  albumFolder,
  jerseyColors = [],
) => {
  if (!albumFolder) {
    return [];
  }

  const {
    photo_pricing: photoPricing,
    album_folder_print_packages: albumFolderPrintPackages,
    print_assets: albumFolderPrintAssets,
  } = albumFolder;

  // If there are print assets directly in the album folder, use only those
  if (albumFolderPrintAssets?.records?.length > 0) {
    const assets = (albumFolderPrintAssets.records ?? [])
      .filter(
        ({ print_asset: printAsset }) =>
          printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
      )
      .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);

    return filterPrintAssetsByJerseyColors(assets, jerseyColors);
  }

  const packageAssetBackgrounds = flatten(
    (albumFolderPrintPackages?.records ?? []).map(albumFolderPrintPackage => {
      const printPackage = albumFolderPrintPackage?.print_package;

      if (!printPackage) {
        return [];
      }

      return (printPackage?.print_assets?.records ?? [])
        .filter(
          ({ print_asset: printAsset }) =>
            printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
        )
        .map(printPackagePrintAsset => printPackagePrintAsset.print_asset);
    }),
  );

  const photoPricingAssetsBackgrounds = photoPricing
    ? (photoPricing?.print_assets?.records ?? [])
        .filter(
          ({ print_asset: printAsset }) =>
            printAsset.type === PRINT_ASSET_TYPES.BACKGROUND,
        )
        .map(printPackagePrintAsset => printPackagePrintAsset.print_asset)
    : [];

  const printAssetBackgrounds = uniqBy(
    [...packageAssetBackgrounds, ...photoPricingAssetsBackgrounds],
    asset => asset.id,
  );

  return filterPrintAssetsByJerseyColors(printAssetBackgrounds, jerseyColors);
};
